import React, { useState, useEffect } from 'react';
import DataList from '../DataList';
import { getFormattedDate } from '../../assets/Utils';
import ApiConfigFactory from '../../configurations/ApiConfigFactory';
import axios from 'axios';
import { useAuth } from '../../hooks/useAuth';

const LabTestList = () => {
  const [availableTests, setAvailableTests] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const { token } = useAuth();

  // Fetch active non-kit tests for kit_components dropdown
  useEffect(() => {
    const fetchAvailableTests = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/lab/labTests?is_active=1&is_kit=0`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const testOptions = response.data.map(test => ({
          value: test.id,
          label: `${test.test_name} (${test.category} - ${test.type})`
        }));
        setAvailableTests(testOptions);
        console.log('Available tests for kits:', testOptions);
      } catch (error) {
        console.error('Error fetching available tests for kits:', error);
      }
    };

    fetchAvailableTests();
  }, [token]);

  // Initialize apiConfig using ApiConfigFactory
  const apiConfig = new ApiConfigFactory('lab/labTests', 'Lab Tests');
  
  // Apply filter based on toggle state - always append is_active parameter
  if (showInactive) {
    apiConfig.appendQueryParam('is_active', '0'); // Show only inactive
  } else {
    apiConfig.appendQueryParam('is_active', '1'); // Show only active
  }
  
  apiConfig.defaultSortAsc = false;
  apiConfig.defaultSortFieldId = 'id';
  apiConfig.path = '/labTests';
  apiConfig.gcBucketIdentifier = '';//use default for the main patient object.
  apiConfig.cloneTransform = (clonedRecord, sourceRecord) => ({
    ...clonedRecord,
    test_name: sourceRecord?.test_name ? `${sourceRecord.test_name} (Copy)` : 'New Test Copy',
  });
  apiConfig.bulkActions = [
    { name: 'Clone', type: 'clone' },
    { name: 'Generate Report', endpoint: '/hospital-portal/api/medical-contracts/generate-report' },
  ];
  // Configure fields, columns, and other properties
  apiConfig
    .addField({ name: 'id', label: 'ID', type: 'text', identifier: true, visible: false, tab: 'Main' })
    .addField({ name: 'category', label: 'Category', type: 'dropdown', tab: 'Main', isCreatable: true, creatablePermission: '/labTests/configuration',width:'250px' })
    .addField({ name: 'type', label: 'Type', type: 'dropdown', required: true, tab: 'Main', isCreatable: true, creatablePermission: '/labTests/configuration',width:'250px' })
    .addField({ name: 'test_name', label: 'Test Name', type: 'text', tab: 'Main' })
    .addField({ name: 'test_unit', label: 'Unit', type: 'text', required: false, tab: 'Main' })
    .addField({ name: 'test_reference_range', label: 'Range', type: 'text', required: true, tab: 'Main' })
    .addField({ name: 'lab_type', label: 'Laboratory/Radiology', type: 'dropdown', required: false, tab: 'Main' })
    .addField({ name: 'side_required', label: 'Side Required', type: 'checkbox', defaultValue: false, tab: 'Main' })
    .addField({ name: 'note', label: 'Note', type: 'text', tab: 'Main' })
    .addField({ name: 'is_active', label: 'Active', type: 'checkbox', defaultValue: true, tab: 'Main' })
    .addField({ name: 'item_id', label: 'Zoho Item Mapping', type: 'dropdown', tab: 'Configuration' })
    .addField({ name: 'is_kit', label: 'Is Kit', type: 'checkbox', defaultValue: false, tab: 'Configuration' })
    .addField({
      name: 'kit_components',
      label: 'Kit Components',
      type: 'subform',
      subFields: [
        {
          name: 'test_id',
          label: 'Test',
          type: 'dropdown',
          width: '300px',
          required: true,
          options: availableTests
        }
      ],
      tab: 'Configuration',
      visible: true
    })
   
    .addStaticDropdownField({
      name: 'lab_type',
      options: [{ id: 'Laboratory', name: 'Laboratory' }, { id: 'Radiology', name: 'Radiology' }],
    })
    .addStaticDropdownField({
      name: 'patient_type',
      options: [{ id: 'Private', name: 'Private' }, { id: 'Company', name: 'Company' }],
    })
    .addDropdownField({
      name: 'item_id',
      endpoint: '/hospital-portal/api/items',
      valueField: 'item_id',
      labelField: 'item_name',
    })
    .addDropdownField({
      name: 'category',
      endpoint: '/hospital-portal/api/lab/labTests/options/categories-types',
      valueField: 'value',
      labelField: 'label',
      transformResponse: (data) => data.categories.map(cat => ({ value: cat, label: cat }))
    })
    .addDropdownField({
      name: 'type',
      endpoint: '/hospital-portal/api/lab/labTests/options/categories-types',
      valueField: 'value',
      labelField: 'label',
      transformResponse: (data) => data.types.map(type => ({ value: type, label: type }))
    })
    .addTab({ name: 'Main', roles: 'all', default: true })
    .addTab({ name: 'Configuration', securityPath: '/labTests/configuration', permission: 'View', editPermission: 'Edit' })

    .addColumn({ id: 'id', name: ' ID', selector: (row) => row.id, type: 'number', sortable: true, identifier: true, width: '75px' })
    .addColumn({ name: 'Category', selector: (row) => row.category, sortable: true, filterable: true, filterSelector: 'category'})
    .addColumn({ name: 'Type', selector: (row) => row.type, sortable: true, filterable: true, filterSelector: 'type' })
    .addColumn({ name: 'Test Name', selector: (row) => row.test_name, sortable: true, filterable: true, filterSelector: 'test_name' })
    .addColumn({ name: 'Is Kit', selector: (row) => row.is_kit ? 'Yes' : 'No', sortable: true, filterable: true, filterSelector: 'is_kit', width: '75px' })
    .addColumn({ name: 'Active', selector: (row) => row.is_active ? 'Yes' : 'No', sortable: true, filterable: true, filterSelector: 'is_active', width: '75px' })
    .addColumn( { name: 'Lab/Rdlgy', selector: (row) => row.lab_type, sortable: true, filterable: true, filterSelector: 'lab_type' })
    .addColumn( { name: 'Zoho Item', selector: (row) => row.Item?.item_name || row.item_id || '-', sortable: true, filterable: true, filterSelector: 'item_id' })

  const handleToggleChange = () => {
    setShowInactive(!showInactive);
    setRefreshKey(prev => prev + 1);
  };
  
  return (
    <div>
      <div style={{ padding: '10px', backgroundColor: '#f5f5f5', borderBottom: '1px solid #ddd' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
          <input 
            type="checkbox" 
            checked={showInactive} 
            onChange={handleToggleChange}
          />
          <span>Show Inactive</span>
        </label>
      </div>
      <DataList key={refreshKey} apiConfig={apiConfig} columns={apiConfig.columns} />
    </div>
  );
};

export default LabTestList;
